/**
 * Event envelope (per spec §11 + §23 — single event per message).
 * Drives both the Events tab and the Live Alerts pane.
 */
import { z } from 'zod';

export const EventCategory = z.enum([
  'System',
  'Mission',
  'Power',
  'Comms',
  'DAA',
  'Surveillance',
  'Infrastructure',
  'OI',
  // Per the BE job/category mapping (2026-08-13): TISB (CIFIB rebroadcast)
  // and Operational Assets (am-nid-dd-job) are real, distinct categories —
  // previously folded into 'System' before this mapping existed.
  'TISB',
  'Operational Assets',
]);

export const EventSeverity = z.enum(['crit', 'warn', 'info', 'ok']);

export const EventEnvelope = z.object({
  event_id: z.string(),
  drone_id: z.string().nullable(),
  timestamp: z.string().datetime(),
  category: EventCategory,
  severity: EventSeverity,
  message: z.string(),
  /** UI nav-context ids this event routes to (already mapped from BE's
   *  raw `contexts` — see `lib/mqtt/contextMap.ts`). Empty when the
   *  producing job doesn't set `contexts` yet — callers fall back to
   *  category-based routing in that case (`features/alerts/routing.ts`). */
  contexts: z.array(z.string()).default([]),
  // For DAA conflicts (per spec §23.4)
  conflict_target: z.string().nullable().optional(),
  ttc_sec: z.number().nullable().optional(),
  dist_km: z.number().nullable().optional(),
  ack_required: z.boolean().default(false),
  acknowledged: z.boolean().default(false),
  /** Tenant that produced this event, distinct from `tenantId` (the
   *  receiving broker's own tenant — constant per connection, so not
   *  meaningful data). Mirrors `TelemetryEnvelope.partner_tenant`: null
   *  for every event today since sandbox has no partner drones, but the
   *  field is what will identify a partner tenant's event once
   *  cross-tenant drones exist. Not present on every job's events yet —
   *  see output/events.ts. */
  source_tenant: z.string().nullable().optional(),
});

export type EventCategory = z.infer<typeof EventCategory>;
export type EventSeverity = z.infer<typeof EventSeverity>;
export type EventEnvelope = z.infer<typeof EventEnvelope>;
